"use client";

import { type ResultOf } from "gql.tada";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useDateFormatter } from "react-aria";

import { usePages } from "@trip/react-hooks";
import { swipePower } from "@trip/utils";

import { AngleLeftIcon } from "~/components/icons/angle-left";
import { AngleRightIcon } from "~/components/icons/angle-right";
import { Image } from "~/components/image";
import { Rating } from "~/components/rating";
import { Button } from "~/components/ui/button";

import type { TRIP_QUERY } from "../_lib/gql";

type Direction = -1 | 1;

export function Reviews({
  reviews,
}: {
  reviews: NonNullable<ResultOf<typeof TRIP_QUERY>["trip"]>["reviews"];
}) {
  const tAccessibility = useTranslations("Accessibility");
  const swipeConfidenceThreshold = 10_000;

  const { direction, paginate, index, item: review } = usePages(reviews);
  const formatDate = useDateFormatter({
    dateStyle: "medium",
    timeStyle: undefined,
  });

  if (!review) {
    return;
  }

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="relative grow overflow-hidden">
        <AnimatePresence custom={direction} initial={false}>
          <motion.div
            animate="center"
            className="absolute inset-2"
            custom={direction}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            exit="exit"
            initial="initial"
            key={`chunk-${index}`}
            onDragEnd={(_, { offset, velocity }) => {
              const swipe = swipePower(offset.x, velocity.x);
              if (swipe < -swipeConfidenceThreshold) {
                paginate(1);
              } else if (swipe > swipeConfidenceThreshold) {
                paginate(-1);
              }
            }}
            transition={{
              opacity: { duration: 0.2 },
              x: { damping: 30, stiffness: 300, type: "spring" },
            }}
            variants={{
              center: {
                opacity: 1,
                x: 0,
              },
              exit: (direction: Direction) => ({
                opacity: [1, 1, 0],
                x: direction === 1 ? "-20%" : "20%",
              }),
              initial: (direction: Direction) => ({
                opacity: [0, 1, 1],
                x: direction === 1 ? "20%" : "-20%",
              }),
            }}
          >
            <div className="text-orange mb-4 flex items-center gap-4">
              <Rating value={review.rating} />
              <div className="text-sm font-medium text-white">
                {review.rating}
              </div>
            </div>
            <div className="text-sm font-light xl:text-base">
              {review.description}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="flex items-center justify-between gap-4 p-2">
        <div className="flex items-center gap-4">
          {review.profilePicture?.responsiveImage ? (
            <Image
              className="shrink-0 overflow-hidden [&_img]:rounded-full [&_img]:shadow-[inset_0_0_0_1px_rgba(0,0,0,.1)]"
              layout="fixed"
              data={review.profilePicture.responsiveImage}
            />
          ) : null}
          <div className="grow">
            <div className="text-sm font-light leading-tight">
              {review.name}
            </div>
            <div className="text-xs font-light">
              {formatDate.format(new Date(review.date))}
            </div>
          </div>
        </div>

        {reviews.length > 1 ? (
          <div className="hidden justify-end gap-1 sm:flex">
            <Button
              onClick={(event) => {
                event.preventDefault();
                paginate(-1);
              }}
              size="circle"
              className="text-neutral-900"
            >
              <span className="sr-only">{tAccessibility("left")}</span>
              <AngleLeftIcon />
            </Button>
            <Button
              onClick={(event) => {
                event.preventDefault();
                paginate(1);
              }}
              size="circle"
              className="text-neutral-900"
            >
              <span className="sr-only">{tAccessibility("right")}</span>
              <AngleRightIcon />
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
